import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { lazy, Suspense } from "react";
import { useClarity } from "@/hooks/useClarity";
import { LEAD_MAGNET_PATH, LEAD_MAGNET_THANKYOU_PATH } from "@shared/lead-magnet";

// Core components - loaded immediately
import Header from "./components/Header";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import ScrollToTop from "./components/ScrollToTop";
import { SiteAlertSystem } from "./components/SiteAlertSystem";
import { NewVersionBanner } from '@/components/NewVersionBanner';

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground">Caricamento...</p>
    </div>
  </div>
);

// ========================================
// LAZY LOADED PAGES - Code Splitting
// Each page loads only when visited
// ========================================

// Main Public Pages
const Home = lazy(() => import("@/pages/Home"));
const MetodoPage = lazy(() => import("@/pages/MetodoPage"));
const PaoloBianchiniPage = lazy(() => import("@/pages/PaoloBianchiniPage"));
const TestimonianzePage = lazy(() => import("@/pages/TestimonianzePage"));
const FormazionePage = lazy(() => import("@/pages/FormazionePage"));
const AssistenzaPage = lazy(() => import("@/pages/AssistenzaPage"));
const ShopPage = lazy(() => import("@/pages/ShopPage"));
const ContattiPage = lazy(() => import("@/pages/ContattiPage"));
const PremiumPage = lazy(() => import("@/pages/PremiumPage"));
const PressPage = lazy(() => import("@/pages/PressPage"));
const InternationalPage = lazy(() => import("@/pages/InternationalPage"));
const RecipesPage = lazy(() => import("@/pages/RecipesPage"));

// Course Pages
const CorsoCucinaPage = lazy(() => import("@/pages/CorsoCucinaPage"));
const CorsoPasticceriaPage = lazy(() => import("@/pages/CorsoPasticceriaPage"));
const CoursePlayerPage = lazy(() => import("@/pages/CoursePlayerPage"));
const PrenotaConsulenza = lazy(() => import("@/pages/PrenotaConsulenza"));

// Success/Thank You Pages
const ShopSuccessPage = lazy(() => import("@/pages/ShopSuccessPage"));
const RecipePurchaseSuccess = lazy(() => import("@/pages/RecipePurchaseSuccess"));
const ThankYouPage = lazy(() => import("@/pages/ThankYouPage"));

// Protected Client Pages
const VideoPage = lazy(() => import("@/pages/VideoPage"));
const AreaClienti = lazy(() => import("@/pages/AreaClienti"));

// Riflessioni (il "blog" del sito) e pagine di dettaglio
const RiflessioniPage = lazy(() => import("@/pages/RiflessioniPage"));
const RiflessionePage = lazy(() => import("@/pages/RiflessionePage"));
const PressMentionPage = lazy(() => import("@/pages/PressMentionPage"));

// Legal Pages
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsConditionsPage = lazy(() => import("@/pages/TermsConditionsPage"));
const ConsensoInformatoPage = lazy(() => import("@/pages/ConsensoInformatoPage"));
const CookiePolicyPage = lazy(() => import("@/pages/CookiePolicyPage"));

// Utility Pages
const NotFound = lazy(() => import("@/pages/not-found"));

// Landing Pages (standalone, no global header/footer)
const LandingPrimoAppuntamento = lazy(() => import("@/pages/LandingPrimoAppuntamento"));
const LandingRicettaOmaggio = lazy(() => import("@/pages/LandingRicettaOmaggio"));
const GrazieRicettaPage = lazy(() => import("@/pages/GrazieRicettaPage"));

// Admin Pages (separate chunk)
const SupabaseAdminLogin = lazy(() =>
  import("@/pages/SupabaseAdminLogin").then(m => ({ default: m.SupabaseAdminLogin }))
);
const MobileAdminDashboardV2 = lazy(() => import("@/pages/MobileAdminDashboardV2"));
const ProspettiPage = lazy(() => import("@/pages/ProspettiPage"));
const ClientProfilePage = lazy(() => import("@/pages/ClientProfilePage"));

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/metodo" component={MetodoPage} />
        <Route path="/paolo-bianchini" component={PaoloBianchiniPage} />
        <Route path="/testimonianze" component={TestimonianzePage} />
        <Route path="/formazione" component={FormazionePage} />
        <Route path="/shop" component={ShopPage} />
        <Route path="/shop/success" component={ShopSuccessPage} />
        <Route path="/supporto" component={AssistenzaPage} />
        <Route path="/assistenza" component={AssistenzaPage} />
        <Route path="/ricette" component={RecipesPage} />
        <Route path="/recipes/:id/success">
          {(params) => <RecipePurchaseSuccess />}
        </Route>
        <Route path="/video">
          {() => (
            <ProtectedRoute allowedUserTypes={['client']} redirectTo="/area-clienti">
              <VideoPage />
            </ProtectedRoute>
          )}
        </Route>

        {/* Area Clienti */}
        <Route path="/area-clienti">
          {() => (
            <ProtectedRoute allowedUserTypes={['client']} redirectTo="/area-clienti">
              <AreaClienti />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/prenota-consulenza" component={PrenotaConsulenza} />
        <Route path="/consulenza-confermata" component={ThankYouPage} />
        <Route path="/contatti" component={ContattiPage} />
        <Route path="/privacy-policy" component={PrivacyPolicyPage} />
        <Route path="/termini-condizioni" component={TermsConditionsPage} />
        <Route path="/consenso-informato" component={ConsensoInformatoPage} />
        <Route path="/cookie-policy" component={CookiePolicyPage} />
        <Route path="/corso-cucina" component={CorsoCucinaPage} />
        <Route path="/corso-pasticceria" component={CorsoPasticceriaPage} />
        <Route path="/courses/:id/watch" component={CoursePlayerPage} />
        <Route path="/premium" component={PremiumPage} />
        <Route path="/press" component={PressPage} />
        <Route path="/international" component={InternationalPage} />
        <Route path="/primo-appuntamento" component={LandingPrimoAppuntamento} />

        {/* Funnel lead magnet "Cremoso al Limone" — landing + thank-you page.
            NON sostituisce /primo-appuntamento, che resta online e invariata. */}
        <Route path={LEAD_MAGNET_PATH} component={LandingRicettaOmaggio} />
        <Route path={LEAD_MAGNET_THANKYOU_PATH} component={GrazieRicettaPage} />

        {/* Riflessioni */}
        <Route path="/riflessioni" component={RiflessioniPage} />
        <Route path="/riflessioni/:slug">
          {(params) => <RiflessionePage params={params} />}
        </Route>
        {/* Vecchi indirizzi del blog */}
        <Route path="/blog"><Redirect to="/riflessioni" replace /></Route>
        <Route path="/blog/:slug"><Redirect to="/riflessioni" replace /></Route>

        {/* Press Mentions */}
        <Route path="/parlano-di-noi/:slug">
          {(params) => <PressMentionPage params={params} />}
        </Route>

        {/* Admin Routes */}
        <Route path="/supabase-admin-login" component={SupabaseAdminLogin} />
        <Route path="/admin/gestionale" component={MobileAdminDashboardV2} />
        <Route path="/admin/prospetti" component={ProspettiPage} />
        <Route path="/admin/clients/:id">
          {(params) => (
            <ProtectedRoute adminOnly={true} redirectTo="/supabase-admin-login">
              <ClientProfilePage clientId={parseInt(params.id)} />
            </ProtectedRoute>
          )}
        </Route>

        {/* Fallback to 404 */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function AppContent() {
  const [location] = useLocation();

  // Initialize Microsoft Clarity (GDPR compliant - waits for cookie consent)
  useClarity();

  // Hide header/footer on admin routes and special pages
  const isAdminRoute = location.startsWith('/admin') ||
    location === '/supabase-admin-login';

  // Pages with custom minimal header (no global header/footer)
  const hasCustomHeader = location === '/international' ||
    location === '/primo-appuntamento' ||
    location === LEAD_MAGNET_PATH ||
    location === LEAD_MAGNET_THANKYOU_PATH;

  const hideGlobalLayout = isAdminRoute || hasCustomHeader;

  return (
    <div className="flex flex-col min-h-screen" data-clarity-unmask="true">
      <ScrollToTop />
      {!hideGlobalLayout && <Header />}
      <main className="flex-grow" data-clarity-unmask="true">
        <Router />
      </main>
      {!hideGlobalLayout && <Footer />}
      {!isAdminRoute && !hasCustomHeader && <SiteAlertSystem />}
      {!isAdminRoute && <CookieConsent />}
      {/* Solo nel gestionale: le schede restano aperte per giorni e senza avviso
          si continua a lavorare su una versione vecchia dopo ogni deploy */}
      {isAdminRoute && <NewVersionBanner />}
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppContent />
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
